import {ImportTransformContext} from "./import-transform-context";

/**
 * @abstract
 * Base for engine import transformers.
 *
 * ## Run contract (customers-territories-and-account-managers, part-description, part-notes, …)
 *
 * Implementations should follow this pipeline so preview/final import gather **every** applicable
 * issue per row instead of stopping after the first failure.
 *
 * 1. **`rowState`** — Build one entry per CSV row: `{ index, item, …derived keys…, codes: [] }`.
 *    Push **invalidity codes only** onto `codes` (never `return` early from the whole `run()`).
 *
 * 2. **Phases — always run in order; do not short-circuit the batch**
 *    - **Preliminary (CSV-only):** missing keys, obviously invalid cells, etc.
 *    - **Remote resolution:** e.g. `this.ctx.fetchPartInfoByPartNumbers` / `fetchVendorInfoByVendorCodes`
 *      when there are keys to resolve. If a key set is empty, **skip that HTTP call** only — still
 *      run later phases so other columns can emit issues on the same rows.
 *    - **Post-API row checks:** unknown part id, etc.
 *    - **Transformer-specific:** booleans, required fields, domain rules. Prefer **not** gating
 *      these checks behind `if (partId)` unless a rule truly depends on a resolved id; otherwise
 *      INVALID_PART and other codes cannot merge on one row.
 *
 * 3. **Never** `return { error }` / empty batch solely because a shared lookup failed — surface
 *    row-level codes (e.g. `INVALID_PART`) and continue so other phases still append codes.
 *
 * 4. **Emit `preventativeIssues`** — One issue object per problematic data row:
 *    - Empty / missing `item`: single entry with a domain-appropriate default (e.g. `INVALID_PART`
 *      on part imports, `INVALID_CUSTOMER` on this import).
 *    - Otherwise: `{ rowIndex, originalRow: item, invalidityCodes: [...new Set(rs.codes)] }`.
 *
 * 5. **Build `apiRequests` last** — Use `issueIndexSet` from `preventativeIssues`. Preview rows
 *    with issues get placeholders; final run uses `null` for those indices. Length matches `rows`.
 *
 * @see CustomersTerritoriesAndAccountManagersImportTransformer
 * @see ExternalCustomersImportTransformer
 * @see ExternalProductsImportTransformer
 * @see PartDescriptionImportTransformer
 * @see PartNotesImportTransformer
 * @see PartSerializabilityImportTransformer
 * @see PartTechnicalInformationImportTransformer
 * @see PartUpcImportTransformer
 * @see PartPriceLevelImportTransformer
 * @see PartVisibilityImportTransformer
 * @see PriceMatricesImportTransformer
 * @see ProductDimensionsImportTransformer
 * @see ProductCategoryAssignmentImportTransformer
 * @see ProductPropertyOptionsImportTransformer
 * @see ProductPropertiesImportTransformer
 * @see ProductVendorsImportTransformer
 * @see WarehouseAlertThresholdsImportTransformer
 */
export class BaseImportTransformer {
    static sharedImportTransformContext = new ImportTransformContext();

    /**
     * @param {ImportTransformContext} [context]
     */
    constructor(context) {
        this.ctx = context ?? BaseImportTransformer.sharedImportTransformContext;
    }

    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @returns {Promise<{apiRequests: Array<*>, preventativeIssues: Array<*>}>}
     */
    // eslint-disable-next-line no-unused-vars
    async run(mappedData, isFinalRun) {
        throw new Error(`${this.constructor.name}.run() must be implemented`);
    }
}
